import { defineStore } from "pinia";
import { useQuasar } from "quasar";
import axios from "axios";
import { useAuthenticationStore } from "store/modules/authenticationStore.js";
import { getRedirectPath } from "common/authenticationConstants";
import useHelpers, { getCookie } from "composables/useHelpers";

/**
 * Authorization Store
 *
 * This store manages the roles used in the application.
 */
export const useAuthorizationStore = defineStore("authorization", () => {
  // Quasar
  const $q = useQuasar();

  // Authenthication Store
  const authenticationStore = useAuthenticationStore();
  const { processResults } = authenticationStore;

  // Refs
  const isImpersonating = localStorage.getItem("webmaster_refresh_token")
    ? localStorage.getItem("webmaster_refresh_token")
    : false;

  /**
   * The roles used in the application.
   * @type {Array<Object>}
   * @property {string} label - The display label of the role.
   * @property {string} name - The internal name of the role.
   */
  const allRoles = [
    {
      label: "Webmaster",
      name: "webmaster",
    },
    {
      name: "manager",
      label: "Applicatiebeheerder",
    },
    {
      name: "organizer",
      label: "Organisator",
    },
    {
      name: "teamleader",
      label: "Teamleider",
    },
    {
      name: "decaan",
      label: "Organisator portfolio",
    },
    {
      name: "teacher",
      label: "Docent",
    },
    {
      name: "student",
      label: "Student",
    },
  ];

  const impersonateUser = async ({
    userId = null,
    impersonatedUserType = null,
  } = {}) => {
    if (!userId) {
      $q.notify({
        message: "Geen employeeID meegestuurd.",
        type: "negative",
      });
    }

    try {
      const { data } = await axios.post(`/api/impersonate/${userId}`);

      const webmasterRefreshToken = localStorage.getItem("refresh_token");

      const { user } = useHelpers();

      localStorage.setItem("webmaster_refresh_token", webmasterRefreshToken);
      localStorage.setItem("webmaster_user_id", user.id);
      localStorage.setItem("impersonated_user_type", impersonatedUserType);

      $q.notify({
        message:
          "Je bent wordt nu ingelogd als .... , de pagina wordt opnieuw geladen.",
        type: "positive",
      });

      await processResults(data);

      return data;
    } catch (error) {
      $q.notify({
        message: "Er is iets fout gegaan bij het inloggen.",
        type: "negative",
      });
    }
  };

  const returnAsWebmaster = async () => {
    try {
      const webmasterRefreshToken = localStorage.getItem(
        "webmaster_refresh_token",
      );

      const impersonatedUserType = localStorage.getItem(
        "impersonated_user_type",
      );

      const webmasterUserId = localStorage.getItem("webmaster_user_id");

      const { data } = await axios.post(
        "/api/impersonate/return",
        {
          refresh_token: webmasterRefreshToken,
        },
        {
          headers: {
            TwoFactor: getCookie(`TwoFactor${webmasterUserId}`),
          },
        },
      );

      localStorage.removeItem("webmaster_refresh_token");
      localStorage.removeItem("impersonated_user_type");
      localStorage.removeItem("webmaster_user_id");

      const redirectPath = getRedirectPath(impersonatedUserType);

      await processResults(data, redirectPath);

      return data;
    } catch (error) {
      console.log(error);
      $q.notify({
        message:
          "Er is iets fout gegaan bij het inloggen, je moet opnieuw inloggen. De pagina wordt opnieuw geladen.",
        type: "negative",
      });

      setTimeout(() => {
        localStorage.clear();
        window.location.reload();
      }, 2000);
    }
  };

  return {
    allRoles,
    isImpersonating,
    impersonateUser,
    returnAsWebmaster,
  };
});
